import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import type { Tables } from '@/integrations/supabase/types';

export type JobAssignment = Tables<'job_assignments'>;

export function useJobAssignments(jobId: string | undefined) {
  return useQuery({
    queryKey: ['job-assignments', jobId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('job_assignments')
        .select('*')
        .eq('job_id', jobId!);
      if (error) throw error;
      return data || [];
    },
    enabled: !!jobId,
  });
}

/** Fetch all job assignments for the current user across all jobs */
export function useMyJobAssignments() {
  const { session } = useAuth();
  const userId = session?.user?.id;

  return useQuery({
    queryKey: ['my-job-assignments', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('job_assignments')
        .select('*')
        .eq('user_id', userId!);
      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
  });
}

export function useIsJobOwner(jobId: string | undefined) {
  const { session } = useAuth();
  const { data: assignments } = useJobAssignments(jobId);
  const userId = session?.user?.id;
  if (!userId || !assignments) return false;
  return assignments.some(a => a.user_id === userId && a.assignment_role === 'owner');
}

export function useAssignToJob() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ jobId, userId, note }: { jobId: string; userId: string; note?: string }) => {
      const currentUser = (await supabase.auth.getUser()).data.user?.id;
      const { error } = await supabase
        .from('job_assignments')
        .insert({
          job_id: jobId,
          user_id: userId,
          assignment_role: 'collaborator',
          assigned_by: currentUser,
          note: note || null,
        });
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['job-assignments', vars.jobId] });
      qc.invalidateQueries({ queryKey: ['my-job-assignments'] });
      toast({ title: 'Team member assigned to job' });
    },
    onError: (error: unknown) => {
      const msg = error instanceof Error && error.message.includes('duplicate') ? 'User is already assigned' : 'Failed to assign';
      toast({ title: msg, variant: 'destructive' });
    },
  });
}

export function useRemoveFromJob() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ jobId, userId }: { jobId: string; userId: string }) => {
      const { error } = await supabase
        .from('job_assignments')
        .delete()
        .eq('job_id', jobId)
        .eq('user_id', userId);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['job-assignments', vars.jobId] });
      qc.invalidateQueries({ queryKey: ['my-job-assignments'] });
      toast({ title: 'Team member removed from job' });
    },
    onError: () => {
      toast({ title: 'Failed to remove assignment', variant: 'destructive' });
    },
  });
}
